<template>
  <li
    class="tw-relative tw-rounded tw-cursor-pointer"
    :class="[groupClass]"
    data-testid="tree-menu-item"
    @click.prevent.stop="onItemClicked($event, item)"
  >
    <slot name="accent" :item-for-slot="item">
      <tree-menu-accent v-if="selected" />
    </slot>
    <div class="tw-relative tw-z-10">
      <span
        v-if="hasChildren"
        class="sf-tree-menu__group-title hover:tw-bg-gray-200"
        :class="titleClass"
        @click.stop="toggle(item?.id)"
      >
        <i
          v-if="hasChildren"
          class="icon sf-tree-menu__group-title-icon tw-mr-1"
          :class="iconClass"
          :data-testid="`tree-menu-icon-${item?.name || item?.id}`"
          @click.stop="toggle(item?.id)"
        />

        <a
          class="
            sf-tree-menu__item-link
            tw-text-sf-gray
            active:tw-text-sf-gray active:tw-opacity-100
            focus:tw-text-sf-gray focus:tw-opacity-100
            hover:tw-text-sf-gray hover:tw-opacity-100
            tw-pointer-events-none
          "
          :class="linkClass"
          :href="ctrlClickPath"
          @click.prevent.stop="onItemClicked($event, item)"
        >
          <span v-dompurify-html="item.name" />
          <slot name="itemSlot" :item-for-slot="item" />
        </a>
      </span>

      <a
        v-if="!hasChildren"
        class="
          sf-tree-menu__item-link
          tw-text-sf-gray
          active:tw-text-sf-gray active:tw-opacity-100
          focus:tw-text-sf-gray focus:tw-opacity-100
          hover:tw-text-sf-gray hover:tw-opacity-100
        "
        :class="linkClass"
        :href="ctrlClickPath"
        @click.prevent.stop="onItemClicked($event, item)"
      >
        <span v-dompurify-html="item.name" />
        <slot name="itemSlot" :item-for-slot="item" />
      </a>

      <tree-menu
        v-if="hasChildren"
        v-show="expanded"
        :level="parentLevel + 1"
        :items="item.children"
        :expanded-items="expandedItems"
        :variant="variant"
        :ctrl-click="ctrlClick"
        @item-expanded="handleItemExpanded"
        @item-highlighted="handleItemHighlighted"
        @item-toggled="toggle"
        @item-clicked="itemClicked"
      >
        <template #accent="{ itemForSlot }">
          <slot name="accent" :item-for-slot="itemForSlot" />
        </template>
        <template #itemSlot="{ itemForSlot }">
          <slot name="itemSlot" :item-for-slot="itemForSlot" />
        </template>
      </tree-menu>
    </div>
    <a
      v-if="ctrlClick && !hasChildren"
      :href="path"
      class="tw-w-full tw-absolute tw-h-full"
    ></a>
  </li>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import TreeMenuAccent from './tree-menu-accent.vue'

export default {
  name: 'TreeMenuItem',
  components: {
    TreeMenuAccent,
    TreeMenu: defineAsyncComponent(() => import('./tree-menu')),
  },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    parentLevel: {
      type: Number,
      required: true,
    },
    expandedItems: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      required: false,
    },
    variant: {
      type: String,
      default: 'default',
    },
    ctrlClick: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['item-clicked', 'item-expanded', 'item-highlighted', 'item-toggled'],
  computed: {
    expanded() {
      return this.expandedItems[this.item.id]
    },
    iconClass() {
      return this.expanded ? 'icon-angle-down' : 'icon-angle-right'
    },
    groupClass() {
      return {
        'sf-tree-menu__group': this.hasChildren,
        'sf-tree-menu__item': !this.hasChildren,
        'tw-pl-2': this.parentLevel === 1 && this.variant === 'default',
        ['hover:tw-bg-gray-200']: !this.hasChildren,
      }
    },
    linkClass() {
      return {
        'tw-whitespace-normal': this.variant === 'default',
      }
    },
    titleClass() {
      return this.parentLevel === 1
        ? 'sf-tree-menu__group-title--top-level'
        : ''
    },
    hasChildren() {
      return this.item.children && this.item.children.length
    },
    path() {
      return this.item?.path || '#'
    },
    ctrlClickPath() {
      return this.ctrlClick ? this.item?.path : '#'
    },
  },
  created() {
    if (this.selected) {
      this.$emit('item-highlighted', this.item.id)
    }
  },
  methods: {
    expandSelf: function () {
      this.$emit('item-expanded', this.item.id)
    },
    handleItemExpanded: function (itemId) {
      this.$emit('item-expanded', itemId)
    },
    handleItemHighlighted: function (itemId) {
      if (this.hasChildren) {
        this.expandSelf()
      }

      this.$emit('item-highlighted', itemId)
    },
    itemClicked: function (data) {
      this.$emit('item-clicked', data)
    },
    toggle(itemId) {
      this.$emit('item-toggled', itemId)
    },
    onItemClicked(event, item) {
      const shouldOpenInNewTab =
        this.ctrlClick && (event.metaKey || event.ctrlKey)

      if (shouldOpenInNewTab) {
        window.open(item.path, '_blank')
        return
      }

      this.$emit('item-clicked', item)
    },
  },
}
</script>
